import { useMutation, useQueryClient } from '@tanstack/react-query';

import { deleteWorkspaceFile } from '@/lib/api/generated/api';
import { expectStatus } from '@/lib/api/client';
import type { FileListing } from '@/lib/api/generated/model';
import { fileKeys } from '../queryKeys';

/**
 * Deletes a folder, and everything stored under it, in one request.
 *
 * A folder is a key prefix rather than an object, so the plain delete finds
 * nothing to remove and answers 404 — `recursive` is not a convenience over it
 * but the only way to remove a folder at all. The service walks the prefix and
 * records one `deleted` event per file it removes.
 *
 * This used to be the client's job: walk the folder, then issue one delete per
 * file. That was chatty on anything large and, worse, could leave a folder half
 * removed with no single outcome to report. One call has one outcome.
 *
 * The folder's row is removed optimistically and restored if the delete fails,
 * mirroring `useDeleteWorkspaceFile`.
 */
export function useDeleteWorkspaceFolder(
  workspaceId: string,
  currentPath: string,
) {
  const queryClient = useQueryClient();
  const listingKey = fileKeys.listing(workspaceId, currentPath);

  return useMutation({
    mutationFn: async ({
      folderPath,
      folderName,
    }: {
      folderPath: string;
      folderName: string;
    }) =>
      expectStatus(
        await deleteWorkspaceFile(workspaceId, {
          path: folderPath,
          recursive: true,
        }),
        204,
        `“${folderName}” could not be deleted. Please try again.`,
      ),
    onMutate: async ({
      folderPath,
    }: {
      folderPath: string;
      folderName: string;
    }) => {
      await queryClient.cancelQueries({ queryKey: listingKey });
      const previousListing = queryClient.getQueryData<FileListing | null>(
        listingKey,
      );

      if (previousListing) {
        queryClient.setQueryData(listingKey, {
          ...previousListing,
          entries: previousListing.entries.filter(
            (entry) => entry.path !== folderPath,
          ),
        } satisfies FileListing);
      }

      return { previousListing };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousListing !== undefined) {
        queryClient.setQueryData(listingKey, context.previousListing);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: listingKey });
    },
  });
}
